import { type CountryData, SUPPORTED_COUNTRIES } from '#app/utils/countries.ts'
import {
	COUNTRY_CODE_VALUES,
	COUNTRY_CODES,
	DEFAULT_COUNTRY_CODE,
	type CountryCode,
} from '#types/country.ts'
export { DEFAULT_COUNTRY_CODE } from '#types/country.ts'

const DIGIT_REGEX = /\D/g
const collapseWhitespace = (value: string): string => value.replace(/\s+/g, ' ')

export const sanitizePhoneDigits = (value: string): string =>
	value.replace(DIGIT_REGEX, '')

export const toCountryCode = (
	raw: string | CountryCode | null | undefined,
): CountryCode | null => {
	if (!raw) return null
	if (typeof raw !== 'string') return raw
	const normalized = collapseWhitespace(raw.trim()).toUpperCase()
	if (!normalized) return null
	return (COUNTRY_CODE_VALUES as readonly string[]).includes(normalized)
		? (normalized as CountryCode)
		: null
}

const FALLBACK_COUNTRY: CountryData = (() => {
	const [first] = SUPPORTED_COUNTRIES
	if (!first) {
		throw new Error('SUPPORTED_COUNTRIES must contain at least one entry')
	}
	return first
})()

export const getDefaultCountry = (): CountryData => FALLBACK_COUNTRY

export const resolveCountryByCode = (code: CountryCode): CountryData => {
	const match = SUPPORTED_COUNTRIES.find((country) => country.code === code)
	return match ?? FALLBACK_COUNTRY
}

export const stripDialCodeDigits = (
	digits: string,
	country: CountryData,
): string => {
	if (!digits) return ''
	const dialDigits = sanitizePhoneDigits(country.dialCode ?? '')
	if (dialDigits && digits.startsWith(dialDigits)) {
		return digits.slice(dialDigits.length)
	}
	return digits
}

export const extractLocalPhoneDigits = (
	value: string,
	country: CountryData,
): string => {
	if (!value) return ''
	const digits = sanitizePhoneDigits(value)
	return stripDialCodeDigits(digits, country)
}

export const formatPhoneLocal = (
	digits: string,
	countryCode: CountryCode,
): string => {
	if (countryCode === COUNTRY_CODES.US || countryCode === COUNTRY_CODES.CA) {
		if (!digits) return ''
		if (digits.length <= 3) return digits
		if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
		return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
	}
	if (countryCode === COUNTRY_CODES.GB) {
		if (!digits) return ''
		if (digits.length <= 5) return digits
		return `${digits.slice(0, 5)} ${digits.slice(5, 11)}`
	}
	if (!digits) return ''
	if (digits.length <= 3) return digits
	if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`
	if (digits.length <= 9)
		return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`
	return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`
}

export const inferPhoneCountryCode = (
	value: string | null | undefined,
): CountryCode | null => {
	if (!value) return null
	const normalized = collapseWhitespace(value.trim())
	if (!normalized.startsWith('+')) return null
	let bestMatch: { code: CountryCode; dialLength: number } | null = null
	for (const country of SUPPORTED_COUNTRIES) {
		const dial = country.dialCode
		if (!dial || !normalized.startsWith(dial)) continue
		const code = toCountryCode(country.code)
		if (!code) continue
		if (!bestMatch || dial.length > bestMatch.dialLength) {
			bestMatch = { code, dialLength: dial.length }
		}
	}
	return bestMatch ? bestMatch.code : null
}

export type FormatPhoneNumberOptions = {
	fallbackCountry?: CountryCode
	includeFallbackDialCode?: boolean
}

export const formatPhoneNumber = (
	value: string | null | undefined,
	options: FormatPhoneNumberOptions = {},
): string | null => {
	const {
		fallbackCountry = DEFAULT_COUNTRY_CODE,
		includeFallbackDialCode = false,
	} = options
	if (!value) return null
	const trimmed = value.trim()
	if (!trimmed) return null

	const digits = sanitizePhoneDigits(trimmed)
	if (!digits) return trimmed

	const detectedCountry = inferPhoneCountryCode(trimmed)
	const countryCode = detectedCountry ?? fallbackCountry
	const country = resolveCountryByCode(countryCode)
	const localDigits =
		detectedCountry != null ? stripDialCodeDigits(digits, country) : digits

	if (!localDigits) {
		return detectedCountry ? (country.dialCode ?? trimmed) : trimmed
	}

	if (
		(countryCode === COUNTRY_CODES.US || countryCode === COUNTRY_CODES.CA) &&
		localDigits.length !== 10
	) {
		return trimmed
	}

	if (!detectedCountry && localDigits.length < 7) {
		return trimmed
	}

	const formattedLocal = formatPhoneLocal(localDigits, countryCode)
	if (!formattedLocal) {
		return trimmed
	}

	if (detectedCountry) {
		return country.dialCode
			? `${country.dialCode} ${formattedLocal}`.trim()
			: formattedLocal
	}

	if (includeFallbackDialCode && country.dialCode) {
		return `${country.dialCode} ${formattedLocal}`.trim()
	}

	return formattedLocal
}
